import { FormattedMessage } from 'react-intl';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';

import Logo from '@/components/Logo';
import { Strong } from '@/components/rich-text-formatting';

type Props = {
  isModalOpen: boolean;
  onClose: () => void;
};

const url = {
  chatButtonUrl: 'https://calendly.com/plooto-demo/plootonetwork',
  getStartedButtonUrl:
    'https://www.plooto.com/signup?utm_source=portal&utm_medium=accounting_cta&utm_campaign=experiment_1.1&utm_content=variant_b',
};

const listItems = [
  {
    id: 0,
    title: <FormattedMessage defaultMessage="Connect your Quickbooks, Xero, or NetSuite account" />,
    description: <FormattedMessage defaultMessage="Save time with end to end reconciliation." />,
  },
  {
    id: 1,
    title: <FormattedMessage defaultMessage="Make your first payment" />,
    description: <FormattedMessage defaultMessage="Check bills off your to-do list 2x faster." />,
  },
  {
    id: 2,
    title: <FormattedMessage defaultMessage="Create approval rules for your team" />,
    description: <FormattedMessage defaultMessage="For fully automated payment operations." />,
  },
];

const AccountingSoftwareFreeTrialModal = ({ isModalOpen, onClose }: Props) => {
  const modalHeader = (
    <Grid container spacing={2}>
      <Grid item container spacing={2} xs={12}>
        <Grid item xs={6}>
          <Logo variant="wordmark" color="main" width={68} height={17} />
        </Grid>
        <Grid item xs={6} container justifyContent="flex-end">
          <IconButton edge="end" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h2" mb={2}>
          <FormattedMessage defaultMessage="Automate your payments today!" />
        </Typography>
        <Typography variant="body1">
          <FormattedMessage defaultMessage="Sync transactions directly with your accounting software." />
        </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        sx={{ display: { xs: 'none', sm: 'block' } }} // Hide the image on mobile, show on small screens and above
      >
        <Box
          component="img"
          src="/Content/png/FreeTrialDialogAccountingSoftwares.png"
          alt="Image displaying receivables and payables with amounts in different currencies, and an option to enable 2-way sync with QuickBooks, Xero, and Oracle NetSuite."
          width="100%"
        />
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={isModalOpen}
      maxWidth="sm"
      aria-labelledby="accounting-software-modal-title"
      onClose={onClose}
    >
      <DialogTitle id="accounting-software-modal-title" sx={{ bgcolor: 'grey.100' }}>
        {modalHeader}
      </DialogTitle>

      <DialogContent>
        <List sx={{ py: 2 }}>
          {listItems.map((item) => (
            <ListItem key={item.id} sx={{ py: 0, pl: 0 }} alignItems="flex-start">
              <ListItemAvatar sx={{ minWidth: 50 }}>
                <Avatar sx={{ bgcolor: 'common.white', color: 'info.dark' }}>
                  <CheckBoxOutlinedIcon sx={{ width: 30, height: 30 }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{ variant: 'subtitle1' }}
                secondary={item.description}
                secondaryTypographyProps={{ variant: 'body1' }}
              />
            </ListItem>
          ))}
        </List>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="We are excited for you to try Plooto! To help you hit the ground running, we’ve included <b>10 free domestic transactions</b> that can be used at any point during your 30-day free trial."
            values={{ b: Strong }}
          />
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3 } }}>
        <Grid container justifyContent="center" pb={2} pt={1}>
          <Button
            variant="outlined"
            color="primary"
            component="a"
            href={url.chatButtonUrl}
            target="_blank"
            rel="noopener"
            startIcon={<PeopleIcon />}
            sx={{
              order: { xs: 2, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              mr: { xs: 0, sm: 2 },
              mt: { xs: 2, sm: 0 },
            }}
          >
            <FormattedMessage defaultMessage="Let's Chat" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            component="a"
            href={url.getStartedButtonUrl}
            target="_blank"
            rel="noopener"
            sx={{
              order: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <FormattedMessage defaultMessage="Get Started For Free" />
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default AccountingSoftwareFreeTrialModal;
